'use client'

import { useTranslations } from 'next-intl'
import { usePathname } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'

import { gaayaOverride } from './gaayaContactWidget'

export const CONTACT_WIDGET_TOGGLE_EVENT = 'oasis:contact-widget-toggle'

interface ContactOption {
  id: string
  type: 'whatsapp' | 'telegram' | 'messenger' | 'wechat' | 'line' | 'instagram' | 'phone'
  href: string
  title: string
  icon: string
  bgColor: string
  hoverColor: string
}

interface PhoneContact {
  city: string
  phone: string
  displayPhone: string
}

interface BranchContactOverride {
  contacts: ContactOption[]
  phone?: PhoneContact
}

const branchContactOverrides: Record<string, BranchContactOverride> = {
  gaaya39: gaayaOverride
}

const contactOptions: ContactOption[] = [
  {
    id: 'whatsapp',
    type: 'whatsapp',
    href: 'https://wa.me/6653920111?contact-widget',
    title: 'WhatsApp',
    icon: 'whatsapp',
    bgColor: 'bg-[#25D366]',
    hoverColor: 'hover:bg-[#1da851]'
  },
  {
    id: 'telegram',
    type: 'telegram',
    href: 'https://t.me/oasisspabot?contact-widget',
    title: 'Telegram',
    icon: 'telegram',
    bgColor: 'bg-[#24A1DE]',
    hoverColor: 'hover:bg-[#1e8bc3]'
  },
  {
    id: 'messenger',
    type: 'messenger',
    href: 'https://m.me/193561277185?contact-widget',
    title: 'Facebook Messenger',
    icon: 'messenger',
    bgColor: 'bg-[#1877F2]',
    hoverColor: 'hover:bg-[#1464d8]'
  },
  {
    id: 'wechat',
    type: 'wechat',
    href: 'https://mp.weixin.qq.com/s/QmPT3IYamed8jqj1QBV72w?contact-widget',
    title: 'WeChat',
    icon: 'wechat',
    bgColor: 'bg-[#7BC142]',
    hoverColor: 'hover:bg-[#6ba638]'
  },
  {
    id: 'line',
    type: 'line',
    href: 'https://line.me/ti/p/@oasisspa?contact-widget',
    title: 'Line',
    icon: 'line',
    bgColor: 'bg-[#06C755]',
    hoverColor: 'hover:bg-[#05b04a]'
  },
  {
    id: 'instagram',
    type: 'instagram',
    href: 'https://www.instagram.com/oasisspathailand?contact-widget',
    title: 'Instagram',
    icon: 'instagram',
    bgColor:
      'bg-[radial-gradient(circle_at_30%_107%,#fdf497_0%,#fdf497_5%,#fd5949_45%,#d6249f_60%,#285AEB_90%)]',
    hoverColor: 'hover:opacity-90'
  }
]

export default function ContactWidget() {
  const t = useTranslations()
  const pathname = usePathname()
  const branchMatch = pathname.match(/\/destination\/[^/]+\/([^/]+)/)
  const currentBranch = branchMatch?.[1]?.toLowerCase()
  const branchOverride = currentBranch ? branchContactOverrides[currentBranch] : undefined

  const activeContacts = branchOverride ? branchOverride.contacts : contactOptions

  const phoneContacts: PhoneContact[] = [
    { city: t('Menu.CITY_CNX'), phone: '+6653920111', displayPhone: '+66 53 920 111' },
    { city: t('Menu.CITY_BKK'), phone: '+6622622122', displayPhone: '+66 22 622 122' },
    { city: t('Menu.CITY_PTY'), phone: '+6638115888', displayPhone: '+66 38 115 888' },
    { city: t('Menu.CITY_PKT'), phone: '+6676337777', displayPhone: '+66 76 337 777' }
  ]
  const [isOpen, setIsOpen] = useState(false)
  const [isClosing, setIsClosing] = useState(false)
  const [visibleItems, setVisibleItems] = useState<number>(0)
  const [showCityDropdown, setShowCityDropdown] = useState(false)
  const [selectedCity, setSelectedCity] = useState<PhoneContact | null>(null)
  const [showModal, setShowModal] = useState(false)
  const timeoutRefs = useRef<NodeJS.Timeout[]>([])

  useEffect(() => {
    return () => {
      timeoutRefs.current.forEach(clearTimeout)
    }
  }, [])

  const toggleWidget = () => {
    timeoutRefs.current.forEach(clearTimeout)
    timeoutRefs.current = []

    if (!isOpen) {
      setIsOpen(true)
      setIsClosing(false)
      setVisibleItems(0)

      // Show phone button first (0ms)
      const phoneTimeout = setTimeout(() => {
        setVisibleItems(1)
      }, 0)
      timeoutRefs.current.push(phoneTimeout)

      // Then show social media buttons one by one from bottom to top
      activeContacts.forEach((_, index) => {
        const timeout = setTimeout(
          () => {
            setVisibleItems((prev) => Math.max(prev, index + 2))
          },
          (activeContacts.length - index) * 80
        )
        timeoutRefs.current.push(timeout)
      })
    } else {
      setIsClosing(true)
      setShowCityDropdown(false)

      // Hide items one by one from top to bottom (reverse order of opening)
      activeContacts.forEach((_, index) => {
        const timeout = setTimeout(() => {
          setVisibleItems((prev) => Math.min(prev, activeContacts.length - index))
        }, index * 80)
        timeoutRefs.current.push(timeout)
      })

      // Hide phone button last
      const phoneTimeout = setTimeout(() => {
        setVisibleItems(0)
      }, activeContacts.length * 80)
      timeoutRefs.current.push(phoneTimeout)

      // Hide the widget after all animations complete
      const finalTimeout = setTimeout(
        () => {
          setIsOpen(false)
          setIsClosing(false)
          setVisibleItems(0)
        },
        (activeContacts.length + 1) * 80 + 200
      )
      timeoutRefs.current.push(finalTimeout)
    }
  }

  // `toggleWidget` is rebuilt every render, so the listener reads it through a ref rather than
  // re-subscribing each time.
  const toggleRef = useRef(toggleWidget)

  useEffect(() => {
    toggleRef.current = toggleWidget
  })

  useEffect(() => {
    const handler = () => toggleRef.current()

    window.addEventListener(CONTACT_WIDGET_TOGGLE_EVENT, handler)
    return () => window.removeEventListener(CONTACT_WIDGET_TOGGLE_EVENT, handler)
  }, [])

  const toggleCityDropdown = () => {
    setShowCityDropdown(!showCityDropdown)
  }

  const openModal = (city: PhoneContact) => {
    setSelectedCity(city)
    setShowModal(true)
    setShowCityDropdown(false)
  }

  const closeModal = () => {
    setShowModal(false)
    setSelectedCity(null)
  }

  const renderIcon = (iconType: string) => {
    const iconClass = 'contact-icon'

    switch (iconType) {
      case 'whatsapp':
        return (
          <svg className={iconClass} viewBox='0 0 24 24' fill='currentColor'>
            <path d='M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z' />
          </svg>
        )
      case 'telegram':
        return (
          <svg className={iconClass} viewBox='0 0 24 24' fill='currentColor'>
            <path d='M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z' />
          </svg>
        )
      case 'messenger':
        return (
          <svg className={iconClass} viewBox='0 0 24 24' fill='currentColor'>
            <path d='M9.101 23.691v-7.98H6.627v-3.667h2.474v-1.58c0-4.085 1.848-5.978 5.858-5.978.401 0 .955.042 1.468.103a8.68 8.68 0 0 1 1.141.195v3.325a8.623 8.623 0 0 0-.653-.036 26.805 26.805 0 0 0-.733-.009c-.707 0-1.259.096-1.675.309a1.686 1.686 0 0 0-.679.622c-.258.42-.374.995-.374 1.752v1.297h3.919l-.386 2.103-.287 1.564h-3.246v8.245C19.396 23.238 24 18.179 24 12.044c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.628 3.874 10.35 9.101 11.647Z' />
          </svg>
        )
      case 'wechat':
        return (
          <svg className={iconClass} viewBox='0 0 24 24' fill='currentColor'>
            <path d='M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 10.16 10.16 0 0 0 2.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.348-8.596-6.348zM5.785 5.991c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178A1.17 1.17 0 0 1 4.623 7.17c0-.651.52-1.18 1.162-1.18zm5.813 0c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178 1.17 1.17 0 0 1-1.162-1.178c0-.651.52-1.18 1.162-1.18zm5.34 2.867c-1.797-.052-3.746.512-5.28 1.786-1.72 1.428-2.687 3.72-1.78 6.22.942 2.453 3.666 4.229 6.884 4.229.826 0 1.622-.12 2.361-.336a.722.722 0 0 1 .598.082l1.584.926a.272.272 0 0 0 .14.047c.134 0 .24-.111.24-.247 0-.06-.023-.12-.038-.177l-.327-1.233a.582.582 0 0 1-.023-.156.49.49 0 0 1 .201-.398C23.024 18.48 24 16.82 24 14.98c0-3.21-2.931-5.837-6.656-6.088V8.89c-.135-.01-.27-.027-.407-.03zm-2.53 3.274c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.97-.982zm4.844 0c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.969-.982z' />
          </svg>
        )
      case 'line':
        return (
          <svg className={iconClass} viewBox='0 0 24 24' fill='currentColor'>
            <path d='M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314' />
          </svg>
        )
      case 'instagram':
        return (
          <svg className={iconClass} viewBox='0 0 24 24' fill='currentColor'>
            <path d='M7.0301.084c-1.2768.0602-2.1487.264-2.911.5634-.7888.3075-1.4575.72-2.1228 1.3877-.6652.6677-1.075 1.3368-1.3802 2.127-.2954.7638-.4956 1.6365-.552 2.914-.0564 1.2775-.0689 1.6882-.0626 4.947.0062 3.2586.0206 3.6671.0825 4.9473.061 1.2765.264 2.1482.5635 2.9107.308.7889.72 1.4573 1.388 2.1228.6679.6655 1.3365 1.0743 2.1285 1.38.7632.295 1.6361.4961 2.9134.552 1.2773.056 1.6884.069 4.9462.0627 3.2578-.0062 3.668-.0207 4.9478-.0814 1.28-.0607 2.147-.2652 2.9098-.5633.7889-.3086 1.4578-.72 2.1228-1.3881.665-.6682 1.0745-1.3378 1.3795-2.1284.2957-.7632.4966-1.636.552-2.9124.056-1.2809.0692-1.6898.063-4.948-.0063-3.2583-.021-3.6668-.0817-4.9465-.0607-1.2797-.264-2.1487-.5633-2.9117-.3084-.7889-.72-1.4568-1.3876-2.1228C21.2982 1.33 20.628.9208 19.8378.6165 19.074.321 18.2017.1197 16.9244.0645 15.6471.0093 15.236-.005 11.977.0014 8.718.0076 8.31.0215 7.0301.0839m.1402 21.6932c-1.17-.0509-1.8053-.2453-2.2287-.408-.5606-.216-.96-.4771-1.3819-.895-.422-.4178-.6811-.8186-.9-1.378-.1644-.4234-.3624-1.058-.4171-2.228-.0595-1.2645-.072-1.6442-.079-4.848-.007-3.2037.0053-3.583.0607-4.848.05-1.169.2456-1.805.408-2.2282.216-.5613.4762-.96.895-1.3816.4188-.4217.8184-.6814 1.3783-.9003.423-.1651 1.0575-.3614 2.227-.4171 1.2655-.06 1.6447-.072 4.848-.079 3.2033-.007 3.5835.005 4.8495.0608 1.169.0508 1.8053.2445 2.228.408.5608.216.96.4754 1.3816.895.4217.4194.6816.8176.9005 1.3787.1653.4217.3617 1.056.4169 2.2263.0602 1.2655.0739 1.645.0796 4.848.0058 3.203-.0055 3.5834-.061 4.848-.051 1.17-.245 1.8055-.408 2.2294-.216.5604-.4763.96-.8954 1.3814-.419.4215-.8181.6811-1.3783.9-.4224.1649-1.0577.3617-2.2262.4174-1.2656.0595-1.6448.072-4.8493.079-3.2045.007-3.5825-.006-4.848-.0608M16.953 5.5864A1.44 1.44 0 1 0 18.39 4.144a1.44 1.44 0 0 0-1.437 1.4424M5.8385 12.012c.0067 3.4032 2.7706 6.1557 6.173 6.1493 3.4026-.0065 6.157-2.7701 6.1506-6.1733-.0065-3.4032-2.771-6.1565-6.174-6.1498-3.403.0067-6.156 2.771-6.1496 6.1738M8 12.0077a4 4 0 1 1 4.008 3.9921A3.9996 3.9996 0 0 1 8 12.0077' />
          </svg>
        )
      default:
        return <span className='text-sm md:text-lg'>📞</span>
    }
  }

  return (
    <>
      {/* Floating Contact Widget */}
      <div className='contact-widget'>
        {/* Contact Options */}
        {isOpen && (
          <div className='contact-options'>
            {activeContacts.map((option, index) => {
              const itemVisible = visibleItems > index + 1
              return itemVisible ? (
                <a
                  key={option.id}
                  href={option.href}
                  target='_blank'
                  rel='noopener noreferrer'
                  title={option.title}
                  className={`contact-item contact-btn contact-link ${option.type}-btn ${isClosing ? 'closing' : 'show'
                    } ${option.bgColor} ${!isClosing ? 'animate-soft-in' : ''}`}
                  style={{
                    animationDelay: isClosing ? `${index * 50}ms` : '0ms'
                  }}>
                  <div className='contact-icon'>{renderIcon(option.icon)}</div>
                </a>
              ) : null
            })}

            {/* Phone Button */}
            {visibleItems > 0 && (!branchOverride || branchOverride.phone) && (
              <button
                onClick={() => {
                  if (branchOverride?.phone) {
                    openModal({ ...branchOverride.phone, city: t(branchOverride.phone.city) })
                  } else {
                    toggleCityDropdown()
                  }
                }}
                className={`contact-item contact-btn contact-phone-btn phone-btn ${isClosing ? 'closing' : 'show'} ${!isClosing ? 'animate-soft-in' : ''
                  } bg-theme`}
                style={{
                  animationDelay: isClosing ? `${activeContacts.length * 50}ms` : '0ms'
                }}
                title='Call Us'>
                <div className='contact-icon'>
                  <svg className='contact-icon' viewBox='0 0 24 24' fill='currentColor'>
                    <path d='M20.01 15.38c-1.23 0-2.42-.2-3.53-.56a.977.977 0 00-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z' />
                  </svg>
                </div>
              </button>
            )}
          </div>
        )}

        {/* Main Floating Button */}
        <button
          onClick={toggleWidget}
          className='main-floating-btn'
          aria-label='Toggle contact options'>
          {/* Ping animation rings */}
          <div className='ping-animation absolute inset-0 rounded-full bg-orange-400'></div>
          <div
            className='ping-animation absolute inset-0 rounded-full bg-orange-400'
            style={{ animationDelay: '1s' }}></div>

          {/* Icon */}
          <svg className='main-btn-icon' viewBox='0 0 24 24' fill='currentColor'>
            <path d='M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z' />
          </svg>
        </button>
      </div>

      {/* City Dropdown */}
      {showCityDropdown && (
        <div className='city-dropdown animate-fade-in'>
          {phoneContacts.map((contact) => (
            <button
              key={contact.city}
              onClick={() => openModal(contact)}
              className='city-dropdown-btn'>
              <svg className='city-icon' viewBox='0 0 24 24' fill='currentColor'>
                <path d='M20.01 15.38c-1.23 0-2.42-.2-3.53-.56a.977.977 0 00-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z' />
              </svg>
              <span className='text-sm font-medium md:text-base'>{contact.city}</span>
            </button>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && selectedCity && (
        <div className='modal-backdrop' onClick={closeModal}>
          <div className='modal-content animate-fade-in' onClick={(e) => e.stopPropagation()}>
            {/* Gradient header */}
            <div className='modal-header'>
              <div className='mb-2'>
                <svg className='modal-icon' viewBox='0 0 24 24' fill='currentColor'>
                  <path d='M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z' />
                </svg>
              </div>
              <h2 className='text-2xl font-bold'>{selectedCity.city}</h2>
            </div>

            {/* Content */}
            <div className='modal-body'>
              <div className='mb-6 text-center'>
                <div className='phone-label'>Phone Number</div>
                <div className='phone-number'>{selectedCity.displayPhone}</div>
              </div>

              {/* Action buttons */}
              <div className='space-y-3'>
                <a href={`tel:${selectedCity.phone}`} className='call-button'>
                  <div className='call-button-effect'></div>
                  <svg className='call-icon' viewBox='0 0 24 24' fill='currentColor'>
                    <path d='M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z' />
                  </svg>
                  <span>Call Now</span>
                  <div className='ping-dot'>
                    <div className='ping-inner'></div>
                  </div>
                </a>

                <button onClick={closeModal} className='close-button'>
                  {t('Menu.label_close')}
                </button>
              </div>
            </div>

            {/* Decorative elements */}
            <div className='modal-decoration-1'></div>
            <div className='modal-decoration-2'></div>
          </div>
        </div>
      )}

      {/* Backdrop for closing dropdowns */}
      {showCityDropdown && (
        <div
          className='dropdown-backdrop'
          onClick={() => {
            setShowCityDropdown(false)
          }}
        />
      )}
    </>
  )
}
